import type { TransactionId } from '../../@types/DataTransferObject.js';
import type { IPayable } from '../../@types/Payable.js';
import type { ApiPaymentContext, PaymentContextApi } from './createPaymentContextApi.js';

type ResponseApi<TPayment extends IPayable> = Pick<
  ApiPaymentContext<TPayment>,
  'getUnpaidPayableItems' | 'getPaidPayableItems' | 'getPaidPayableItemsDTO' | 'getPaidAmount'
> & { getTransactionIds: () => TransactionId[] };

/**
 * Creates a response API that's returned to the callee when the payment settles.
 * The response contains relevant information of how the payment went and if every Payable item succeeded etc.
 *
 * @param paymentService - An instance of `PaymentService`
 * @param paymentContextApi - An instance of `createPaymentContextApi`
 * @returns Api object with functions to interact with the payment response
 */
export function createPaymentResponseApi<TPayment extends IPayable>(
  paymentService: unknown,
  paymentContextApi: PaymentContextApi<TPayment>
): ResponseApi<TPayment> {
  const { getUnpaidPayableItems, getPaidPayableItems, getPaidPayableItemsDTO, getPaidAmount } = paymentContextApi;

  const paidPayableItemsDTO = getPaidPayableItemsDTO();

  /**
   * Returns an array paid item transaction ids.
   *
   * @returns An array of transaction ids
   */
  const getTransactionIds = (): TransactionId[] => {
    const transactionIds = paidPayableItemsDTO.map((dto) => dto.transactionId);

    return transactionIds;
  };

  return {
    // re-exported from paymentContextApi
    getUnpaidPayableItems,
    getPaidPayableItems,
    getPaidPayableItemsDTO,
    getPaidAmount,
    getTransactionIds,
  } as const;
}

export type PaymentResponseApi<TPayable extends IPayable> = ReturnType<typeof createPaymentResponseApi<TPayable>>;
